import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { X, ArrowRight, ArrowLeft, Check, MapPin } from 'lucide-react';
import { cn } from '../../lib/utils';
import api from '../../lib/axios';
import { toast } from 'sonner';

// Tour configurations for each role - with navigation support
const TOUR_CONFIGS = {
  client: {
    name: 'Espace Client',
    steps: [
      {
        target: '[data-tour="sidebar"]',
        title: 'Menu de Navigation',
        content: 'Voici votre menu principal. Il vous permet d\'accéder à toutes les sections de votre espace.',
        placement: 'right',
        path: '/dashboard/client',
      },
      {
        target: '[data-tour="sidebar-item-messages"]',
        title: 'Messagerie',
        content: 'Échangez directement avec votre équipe projet. Toutes vos conversations sont centralisées ici.',
        placement: 'right',
        path: '/dashboard/client',
      },
      {
        target: '[data-tour="sidebar-item-mes-rdv"]',
        title: 'Rendez-vous',
        content: 'Planifiez des rendez-vous avec votre responsable de compte selon ses disponibilités.',
        placement: 'right',
        path: '/dashboard/client',
      },
      {
        target: '[data-tour="sidebar-item-production"]',
        title: 'Production',
        content: 'Retrouvez vos projets, forfaits, domaines et faites de nouvelles demandes.',
        placement: 'right',
        path: '/dashboard/client',
      },
      {
        target: '[data-tour="sidebar-item-finances"]',
        title: 'Finances',
        content: 'Gérez votre wallet, consultez vos factures, échéanciers et contrats.',
        placement: 'right',
        path: '/dashboard/client',
      },
      {
        target: '[data-tour="sidebar-item-ressources"]',
        title: 'Ressources',
        content: 'Accédez à la base de connaissances, supports de communication et accès partagés.',
        placement: 'right',
        path: '/dashboard/client',
      },
      {
        target: '[data-tour="header-notifications"]',
        title: 'Notifications',
        content: 'Recevez des alertes pour les nouveaux livrables, messages et mises à jour importantes.',
        placement: 'bottom',
        path: '/dashboard/client',
      },
    ]
  },
  team_member: {
    name: 'Espace Membre',
    steps: [
      {
        target: '[data-tour="sidebar"]',
        title: 'Menu Principal',
        content: 'Votre menu de navigation pour accéder à toutes les fonctionnalités.',
        placement: 'right',
        path: '/dashboard/member',
      },
      {
        target: '[data-tour="sidebar-item-messagerie"]',
        title: 'Messagerie',
        content: 'Échangez avec vos collègues et les clients sur les projets.',
        placement: 'right',
        path: '/dashboard/member',
      },
      {
        target: '[data-tour="sidebar-item-planning"]',
        title: 'Planning',
        content: 'Visualisez et gérez votre emploi du temps, créneaux bloqués et rendez-vous.',
        placement: 'right',
        path: '/dashboard/member',
      },
      {
        target: '[data-tour="sidebar-item-mes-projets"]',
        title: 'Mes Projets',
        content: 'Consultez et gérez les projets qui vous sont assignés.',
        placement: 'right',
        path: '/dashboard/member',
      },
      {
        target: '[data-tour="sidebar-item-mes-horaires"]',
        title: 'Mes Horaires',
        content: 'Déclarez vos heures travaillées et gérez vos disponibilités.',
        placement: 'right',
        path: '/dashboard/member',
      },
      {
        target: '[data-tour="header-notifications"]',
        title: 'Notifications',
        content: 'Restez informé des nouvelles tâches et messages.',
        placement: 'bottom',
        path: '/dashboard/member',
      },
    ]
  },
  affiliate: {
    name: 'Espace Apporteur',
    steps: [
      {
        target: '[data-tour="sidebar"]',
        title: 'Bienvenue !',
        content: 'Votre espace apporteur d\'affaires pour gérer vos leads et suivre vos commissions.',
        placement: 'right',
        path: '/affiliate/dashboard',
      },
      {
        target: '[data-tour="sidebar-item-mes-leads"]',
        title: 'Mes Leads',
        content: 'Ajoutez de nouveaux leads et suivez leur conversion en clients.',
        placement: 'right',
        path: '/affiliate/dashboard',
      },
      {
        target: '[data-tour="sidebar-item-mes-commissions"]',
        title: 'Commissions',
        content: 'Consultez vos commissions gagnées et leur statut de paiement.',
        placement: 'right',
        path: '/affiliate/dashboard',
      },
      {
        target: '[data-tour="header-notifications"]',
        title: 'Notifications',
        content: 'Soyez alerté quand un de vos leads se convertit en client !',
        placement: 'bottom',
        path: '/affiliate/dashboard',
      },
    ]
  },
  agency: {
    name: 'Espace Agence',
    steps: [
      {
        target: '[data-tour="sidebar"]',
        title: 'Menu Principal',
        content: 'Votre centre de commande pour gérer l\'ensemble de l\'agence.',
        placement: 'right',
        path: '/dashboard/agency',
      },
      {
        target: '[data-tour="sidebar-item-agenda"]',
        title: 'Agenda',
        content: 'Gérez votre calendrier, les rendez-vous et créneaux de disponibilité.',
        placement: 'right',
        path: '/dashboard/agency',
      },
      {
        target: '[data-tour="sidebar-item-clients"]',
        title: 'Clients',
        content: 'Gérez vos clients, leurs projets et leurs informations.',
        placement: 'right',
        path: '/dashboard/agency',
      },
      {
        target: '[data-tour="sidebar-item-equipe"]',
        title: 'Équipe',
        content: 'Administrez les membres de votre équipe, leurs contrats et plannings.',
        placement: 'right',
        path: '/dashboard/agency',
      },
      {
        target: '[data-tour="sidebar-item-finances"]',
        title: 'Finances',
        content: 'Suivez la facturation, les paiements et les indicateurs financiers.',
        placement: 'right',
        path: '/dashboard/agency',
      },
      {
        target: '[data-tour="header-notifications"]',
        title: 'Notifications',
        content: 'Alertes importantes : nouveaux clients, paiements, tâches urgentes.',
        placement: 'bottom',
        path: '/dashboard/agency',
      },
    ]
  }
};

// Tooltip component that positions itself relative to target
const TourTooltip = ({ step, stepIndex, totalSteps, onNext, onPrev, onSkip, onFinish, theme, dontShowAgain, setDontShowAgain }) => {
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [targetRect, setTargetRect] = useState(null);
  const tooltipRef = useRef(null);

  useEffect(() => {
    const updatePosition = () => {
      const target = document.querySelector(step.target);
      if (!target) {
        // If target not found, center the tooltip
        setPosition({
          top: window.innerHeight / 2 - 100,
          left: window.innerWidth / 2 - 175,
        });
        setTargetRect(null);
        return;
      }

      const rect = target.getBoundingClientRect();
      setTargetRect(rect);

      const tooltipWidth = 350;
      const tooltipHeight = 250;
      const padding = 16;

      let top, left;

      switch (step.placement) {
        case 'right':
          top = rect.top + rect.height / 2 - tooltipHeight / 2;
          left = rect.right + padding;
          break;
        case 'left':
          top = rect.top + rect.height / 2 - tooltipHeight / 2;
          left = rect.left - tooltipWidth - padding;
          break;
        case 'bottom':
          top = rect.bottom + padding;
          left = rect.left + rect.width / 2 - tooltipWidth / 2;
          break;
        case 'top':
          top = rect.top - tooltipHeight - padding;
          left = rect.left + rect.width / 2 - tooltipWidth / 2;
          break;
        default:
          top = rect.bottom + padding;
          left = rect.left;
      }

      // Keep tooltip within viewport
      top = Math.max(padding, Math.min(top, window.innerHeight - tooltipHeight - padding));
      left = Math.max(padding, Math.min(left, window.innerWidth - tooltipWidth - padding));

      setPosition({ top, left });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition);
    };
  }, [step]);

  const isLastStep = stepIndex === totalSteps - 1;

  return createPortal(
    <>
      {/* Overlay */}
      <div className="fixed inset-0 z-[9998] bg-black/40 transition-opacity duration-300" />
      
      {/* Spotlight on target - cutout effect with glow, without covering the element */}
      {targetRect && (
        <>
          {/* Cutout spotlight - shows the actual element through the overlay */}
          <div
            className="fixed z-[9999] rounded-lg transition-all duration-300"
            style={{
              top: targetRect.top - 8,
              left: targetRect.left - 8,
              width: targetRect.width + 16,
              height: targetRect.height + 16,
              boxShadow: `
                0 0 0 9999px rgba(0,0,0,0.7),
                0 0 30px 8px rgba(59, 130, 246, 0.6),
                0 0 60px 15px rgba(59, 130, 246, 0.3)
              `,
              background: 'transparent',
              pointerEvents: 'none',
            }}
          />
          {/* Static ring around target (no animation) */}
          <div
            className="fixed z-[10000] rounded-lg transition-all duration-300"
            style={{
              top: targetRect.top - 4,
              left: targetRect.left - 4,
              width: targetRect.width + 8,
              height: targetRect.height + 8,
              border: '3px solid #3b82f6',
              boxShadow: '0 0 15px rgba(59, 130, 246, 0.7)',
              pointerEvents: 'none',
            }}
          />
        </>
      )}

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        className="fixed z-[10000] w-[350px] bg-white rounded-xl shadow-2xl animate-in fade-in slide-in-from-bottom-2 duration-300"
        style={{ top: position.top, left: position.left }}
      >
        {/* Progress bar */}
        <div className="h-1 bg-slate-100 rounded-t-xl overflow-hidden">
          <div
            className="h-full transition-all duration-500"
            style={{
              width: `${((stepIndex + 1) / totalSteps) * 100}%`,
              backgroundColor: theme.colors?.accent || '#3b82f6',
            }}
          />
        </div>

        <div className="p-5">
          {/* Header */}
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-medium text-slate-500">
              Étape {stepIndex + 1} / {totalSteps}
            </span>
            <button
              onClick={onSkip}
              className="p-1 rounded-full hover:bg-slate-100 transition-colors"
            >
              <X className="w-4 h-4 text-slate-400" />
            </button>
          </div>

          {/* Content */}
          <h3 
            className="text-lg font-bold mb-2"
            style={{ color: theme.colors?.primary }}
          >
            {step.title}
          </h3>
          <p className="text-sm text-slate-600 leading-relaxed mb-4">
            {step.content}
          </p>

          {/* Don't show again checkbox */}
          <div className="flex items-center gap-2 mb-4 p-2 bg-slate-50 rounded-lg">
            <Checkbox
              id="dontShowTour"
              checked={dontShowAgain}
              onCheckedChange={setDontShowAgain}
            />
            <label
              htmlFor="dontShowTour"
              className="text-xs text-slate-500 cursor-pointer select-none"
            >
              Ne plus afficher cette visite
            </label>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={onPrev}
              disabled={stepIndex === 0}
              className="flex-1"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Précédent
            </Button>
            <Button
              size="sm"
              onClick={isLastStep ? onFinish : onNext}
              className="flex-1 text-white"
              style={{ backgroundColor: theme.colors?.accent }}
            >
              {isLastStep ? (
                <>
                  <Check className="w-4 h-4 mr-1" />
                  Terminer
                </>
              ) : (
                <>
                  Suivant
                  <ArrowRight className="w-4 h-4 ml-1" />
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Step indicators */}
        <div className="flex items-center justify-center gap-1.5 pb-4">
          {Array.from({ length: totalSteps }).map((_, i) => (
            <div
              key={i}
              className={cn(
                "w-2 h-2 rounded-full transition-all duration-300",
                i === stepIndex ? "w-6" : ""
              )}
              style={{
                backgroundColor:
                  i === stepIndex
                    ? theme.colors?.accent
                    : i < stepIndex
                    ? `${theme.colors?.accent}60`
                    : '#e2e8f0',
              }}
            />
          ))}
        </div>
      </div>
    </>,
    document.body
  );
};

const InteractiveTour = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [steps, setSteps] = useState([]);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const getTourKey = useCallback(() => {
    if (!user) return null;
    if (user.role === 'client') return 'client';
    if (user.team_member_type === 'affiliate' || user.role === 'affiliate') return 'affiliate';
    if (user.role === 'team_member') return 'team_member';
    if (user.role === 'agency' || user.role === 'project_manager' || user.role === 'customer_service') return 'agency';
    return null;
  }, [user]);

  const tourKey = getTourKey();

  useEffect(() => {
    if (!user?.id || !tourKey) return;

    const checkTourStatus = async () => {
      try {
        const [prefsRes, settingsRes] = await Promise.all([
          api.get('/users/preferences').catch(() => ({ data: {} })),
          api.get('/settings/guided-tour').catch(() => ({ data: { enabled: true } }))
        ]);

        const prefs = prefsRes.data || {};
        const settings = settingsRes.data || {};

        // Don't run if settings disable tours
        if (settings.enabled === false) return;
        
        // Don't run if interactive tour already completed/skipped
        if (prefs.interactive_tour_completed || prefs.interactive_tour_skipped) return;
        
        // IMPORTANT: Don't run if onboarding not completed (user needs to fill profile first)
        if (!user.onboarding_completed && user.role !== 'agency' && user.role !== 'client') return;
        
        // IMPORTANT: Don't run if the modal tour hasn't been completed yet
        // This ensures the interactive tour starts AFTER the modal tour
        if (!prefs.guided_tour_completed && !prefs.guided_tour_skipped) return;

        const config = TOUR_CONFIGS[tourKey];
        if (config) {
          setSteps(config.steps);
          setTimeout(() => setRun(true), 2000);
        }
      } catch (error) {
        console.error('Error checking tour status:', error);
      }
    };

    checkTourStatus();

    // Listen for modal tour completion event
    const handleGuidedTourCompleted = () => {
      setTimeout(checkTourStatus, 1000);
    };
    
    window.addEventListener('guided-tour-completed', handleGuidedTourCompleted);
    return () => {
      window.removeEventListener('guided-tour-completed', handleGuidedTourCompleted);
    };
  }, [user, tourKey]);

  const saveTourStatus = async (completed, skipped) => {
    try {
      await api.put('/users/preferences', {
        interactive_tour_completed: completed,
        interactive_tour_skipped: skipped || dontShowAgain,
        interactive_tour_completed_at: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error saving tour status:', error);
    }
  };

  const handleNext = () => {
    if (stepIndex < steps.length - 1) {
      setStepIndex(stepIndex + 1);
    }
  };

  const handlePrev = () => {
    if (stepIndex > 0) {
      setStepIndex(stepIndex - 1);
    }
  };

  const handleSkip = async () => {
    setRun(false);
    await saveTourStatus(false, true);
  };

  const handleFinish = async () => {
    setRun(false);
    await saveTourStatus(true, dontShowAgain);
    if (!dontShowAgain) {
      toast.success('Visite terminée ! Vous pouvez la relancer depuis votre profil.');
    }
  };

  if (!run || steps.length === 0) return null;

  return (
    <TourTooltip
      step={steps[stepIndex]}
      stepIndex={stepIndex}
      totalSteps={steps.length}
      onNext={handleNext}
      onPrev={handlePrev}
      onSkip={handleSkip}
      onFinish={handleFinish}
      theme={theme}
      dontShowAgain={dontShowAgain}
      setDontShowAgain={setDontShowAgain}
    />
  );
};

export default InteractiveTour;
